import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import AdminHeader from './components/AdminHeader';
import ImageUpload from '../components/ImageUpload';

// API configuration
const getApiUrl = (endpoint) => {
  const baseUrl = process.env.NODE_ENV === 'production' ? 'https://fciveria.ge' : 'http://localhost:5000';
  return `${baseUrl}${endpoint}`;
};

const SocialStaffForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    position: '',
    age: '',
    experienceYears: '',
    phone: '',
    email: '',
    photoUrl: '',
    description: '',
    isActive: true,
    displayOrder: 0
  });

  useEffect(() => {
    if (isEdit) {
      loadStaff();
    }
  }, [id, isEdit]);

  const loadStaff = async () => {
    try {
      // Get token from the same source as apiService
      const storedUser = localStorage.getItem('fciveria_admin_user');
      const token = storedUser ? JSON.parse(storedUser).token : null;
      
      if (!token) {
        console.error('No auth token found');
        return;
      }
      
      const response = await fetch(getApiUrl(`/api/social-staff/${id}`), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const staff = await response.json();
        setFormData({
          name: staff.name,
          position: staff.position,
          age: staff.age || '',
          experienceYears: staff.experienceYears || '',
          phone: staff.phone || '',
          email: staff.email || '',
          photoUrl: staff.photoUrl || '',
          description: staff.description || '',
          isActive: staff.isActive,
          displayOrder: staff.displayOrder || 0
        });
      } else {
        console.error('Error loading staff:', response.statusText);
      }
    } catch (error) {
      console.error('Error loading staff:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Get token from the same source as apiService
      const storedUser = localStorage.getItem('fciveria_admin_user');
      const token = storedUser ? JSON.parse(storedUser).token : null;
      
      if (!token) {
        console.error('No auth token found');
        setLoading(false);
        return;
      }
      
      const url = isEdit 
        ? getApiUrl(`/api/social-staff/${id}`)
        : getApiUrl('/api/social-staff');
      
      const method = isEdit ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        navigate('/_panel-7c39f8/social-staff');
      } else {
        const error = await response.json();
        console.error('Error saving staff:', error);
        alert('შეცდომა პერსონალის შენახვისას');
      }
    } catch (error) {
      console.error('Error saving staff:', error);
      alert('შეცდომა პერსონალის შენახვისას');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminHeader 
        title={isEdit ? 'სოც მედია პერსონალის რედაქტირება' : 'ახალი სოც მედია პერსონალი'}
        subtitle={isEdit ? 'შეცვალეთ პერსონალის ინფორმაცია' : 'დაამატეთ ახალი სოც მედია პერსონალი'}
      >
        <button
          onClick={() => navigate('/_panel-7c39f8/social-staff')}
          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 font-georgian"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          უკან
        </button>
      </AdminHeader>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white shadow rounded-lg">
          <form onSubmit={handleSubmit} className="space-y-6 p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column */}
              <div className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 font-georgian">
                    სახელი და გვარი *
                  </label>
                  <input
                    type="text"
                    name="name"
                    id="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 font-georgian"
                    placeholder="შეიყვანეთ სახელი და გვარი"
                  />
                </div>

                <div>
                  <label htmlFor="position" className="block text-sm font-medium text-gray-700 font-georgian">
                    პოზიცია *
                  </label>
                  <input
                    type="text"
                    name="position"
                    id="position"
                    required
                    value={formData.position}
                    onChange={handleChange}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 font-georgian"
                    placeholder="მაგ: სოც მედია მენეჯერი"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="age" className="block text-sm font-medium text-gray-700 font-georgian">
                      ასაკი
                    </label>
                    <input
                      type="number"
                      name="age"
                      id="age"
                      value={formData.age}
                      onChange={handleChange}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 font-georgian"
                      placeholder="45"
                    />
                  </div>

                  <div>
                    <label htmlFor="experienceYears" className="block text-sm font-medium text-gray-700 font-georgian">
                      გამოცდილება (წელი)
                    </label>
                    <input
                      type="number"
                      name="experienceYears"
                      id="experienceYears"
                      value={formData.experienceYears}
                      onChange={handleChange}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 font-georgian"
                      placeholder="8"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 font-georgian">
                    ტელეფონი
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    id="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 font-georgian"
                    placeholder="+995 551 20 02 06"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 font-georgian">
                    ელ. ფოსტა
                  </label>
                  <input
                    type="email"
                    name="email"
                    id="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 font-georgian"
                    placeholder="socialmedia@fciveria.ge"
                  />
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-6">
                <ImageUpload
                  value={formData.photoUrl}
                  onChange={(url) => setFormData(prev => ({ ...prev, photoUrl: url }))}
                  category="staff"
                  label="ფოტო"
                  required={false}
                />

                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700 font-georgian">
                    აღწერა
                  </label>
                  <textarea
                    name="description"
                    id="description"
                    rows={6}
                    value={formData.description}
                    onChange={handleChange}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 font-georgian"
                    placeholder="შეიყვანეთ პერსონალის აღწერა და მოვალეობები..."
                  />
                </div>

                <div>
                  <label htmlFor="displayOrder" className="block text-sm font-medium text-gray-700 font-georgian">
                    ჩვენების რიგი
                  </label>
                  <input
                    type="number"
                    name="displayOrder"
                    id="displayOrder"
                    value={formData.displayOrder}
                    onChange={handleChange}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 font-georgian"
                    placeholder="0"
                  />
                  <p className="mt-1 text-sm text-gray-500 font-georgian">
                    ნაკლები რიცხვი უფრო ადრე გამოჩნდება
                  </p>
                </div>

                <div className="flex items-center">
                  <input
                    id="isActive"
                    name="isActive"
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={handleChange}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900 font-georgian">
                    აქტიური
                  </label>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => navigate('/_panel-7c39f8/social-staff')}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 font-georgian"
              >
                გაუქმება
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 font-georgian"
              >
                {loading ? 'ინახება...' : (isEdit ? 'განახლება' : 'შენახვა')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SocialStaffForm;










